import { BookOpenIcon, CheckCircleIcon, PlusIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Box, Button, Group, Paper, Text, Tooltip } from '@mantine/core';
import { ReactNode } from 'react';
import { CV_SECTION_COLORS, CVSectionColorKey } from '../../constants/cvSectionColors';
import { useEditMode } from '../../context/EditModeContext';
import { useValidation } from '../../context/ValidationContext';
import { ChatService } from '../../services/chatService';
import { ValidateableCvSections } from '../../types/cvSections';

interface CVSectionContainerProps {
  children: ReactNode;
  sectionId: string;
  title: string;
  action?: ReactNode;
  subtitle?: ReactNode;
  buttonText?: string;
  onAddClick?: () => void;
  instructions?: ReactNode;
  personId?: number;
  cvId?: number; // CV ID for validation
  getSectionData?: () => string; // Function to get LLM-readable section data
  faqPath?: string; // FAQ topic path (e.g. "professional-summary") — opens /faq/{faqPath} in a new tab
}

export function CVSectionContainer({
  children,
  sectionId,
  title,
  action,
  subtitle,
  buttonText,
  onAddClick,
  instructions,
  personId,
  cvId,
  getSectionData,
  faqPath
}: CVSectionContainerProps) {
  const { isEditMode } = useEditMode();
  const { setValidationResult, setSectionValidating } = useValidation();

  // Check if sectionId is a valid key in CV_SECTION_COLORS
  const isValidSection = sectionId in CV_SECTION_COLORS;
  const colorKey = isValidSection ? sectionId as CVSectionColorKey : 'education'; // Fallback to education if not found
  const sectionColor = CV_SECTION_COLORS[colorKey];

  // Use CSS variables for more reliable styling
  const borderColor = `var(--mantine-color-${sectionColor.color}-6)`;
  const textColor = `var(--mantine-color-${sectionColor.color}-7)`;

  // Generate button if text and click handler are provided
  const actionButton = buttonText && onAddClick ? (
    <Button
      leftSection={<PlusIcon style={{ width: 16, height: 16 }} />}
      variant="light"
      color="brand"
      onClick={onAddClick}
    >
      {buttonText}
    </Button>
  ) : action;

  // Check if section has data to validate
  const hasSectionData = (): boolean => {
    if (!getSectionData) return false;

    try {
      const data = getSectionData();
      // Check if data is empty or contains "no data" messages
      if (!data) return false;

      const lowerData = data.toLowerCase();
      return !(
        lowerData.includes('no ') &&
        (lowerData.includes('found') ||
          lowerData.includes('entries') ||
          lowerData.includes('available'))
      );
    } catch {
      return false;
    }
  };

  const handleValidateSection = async () => {
    if (cvId && getSectionData) {
      // Map sectionId to CvSections enum
      const sectionMapping: Record<string, ValidateableCvSections> = {
        'summary-qualifications': ValidateableCvSections.Summary,
        'project-experience': ValidateableCvSections.ProjectExperience,
        'education': ValidateableCvSections.Education,
        'work-experience': ValidateableCvSections.WorkExperience,
        'certifications': ValidateableCvSections.Certifications,
        'languages': ValidateableCvSections.Languages,
        'training-courses': ValidateableCvSections.Trainings,
      };

      const cvSection = sectionMapping[sectionId];

      if (!cvSection) {
        console.warn(`[CVSectionContainer] No mapping found for sectionId: ${sectionId}`);
        return;
      }

      // Get the section data from the component
      const sectionData = getSectionData();

      if (!sectionData) {
        setValidationResult(cvSection, 'No data available to validate.');
        return;
      }

      setSectionValidating(cvSection, true);
      setValidationResult(cvSection, null);
      try {
        const result = await ChatService.getInstance().sendValidationRequest(
          cvId,
          cvSection,
          sectionData
        );
        setValidationResult(cvSection, result.response);
      } catch (error) {
        setValidationResult(cvSection, typeof error === 'string' ? error : 'Validation failed.');
      } finally {
        setSectionValidating(cvSection, false);
      }
    }
  };

  return (
    <Box id={sectionId} mb="xl">
      <Paper
        p="md"
        radius="md"
        withBorder={true}
        style={{
          borderLeft: `4px solid ${borderColor}`,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0
        }}
      >
        <Group justify="space-between" mb="md">
          <Group gap="xs" align="center">
            <Group gap={8} align="center">
              <Text size="lg" fw={700} c={textColor}>
                {title}
              </Text>

              {instructions && (
                <Tooltip
                  label={instructions}
                  multiline
                  w={400}
                  position="right"
                  withArrow
                >
                  <Box style={{ cursor: 'help', display: 'flex', alignItems: 'center' }}>
                    <QuestionMarkCircleIcon style={{ width: 20, height: 20 }} />
                  </Box>
                </Tooltip>
              )}

              {faqPath && (
                <Tooltip label="Read more in FAQ" position="right" withArrow>
                  <ActionIcon
                    component="a"
                    href={`/faq/${faqPath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtle"
                    color="brand"
                    aria-label="Read more in FAQ"
                  >
                    <BookOpenIcon style={{ width: 20, height: 20 }} />
                  </ActionIcon>
                </Tooltip>
              )}

              {isEditMode && cvId && hasSectionData() && (
                <Tooltip
                  label="Validate this section"
                  position="right"
                  withArrow
                >
                  <ActionIcon
                    variant="subtle"
                    color="teal"
                    onClick={handleValidateSection}
                    style={{ cursor: 'pointer' }}
                  >
                    <CheckCircleIcon style={{ width: 20, height: 20 }} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>

            {subtitle && <Box>{subtitle}</Box>}
          </Group>
          {actionButton}
        </Group>
        {children}
      </Paper>
    </Box>
  );
} 
